// filter bar to provide advanced querying options for the user i.e. search by antigen type, repeat unit size, branched status, specific residues, and sort by ascending or descending repeat unit size

import { Icon, Button } from 'semantic-ui-react';
import '../../pages/css/databasehome.css';
import { useState, useEffect } from 'react';

// helper functions
import databaseUtils from '../databaseUtils';

// components
import FilterModal from './FilterModal';

//FilterBar operates on the current data from search
export default function FilterBar({ data, onFilterData, setFilterResponse, setSearch, 
    openFilter, toggleFilterOptions, maxRepeatUnit, antigenTypes, clearFlag, setClearFlag }) {
    // filter field states
    const [branched, setBranched] = useState('');
    const [antigenType, setAntigenType] = useState('');
    const [repeatedUnits, setRepeatedUnits] = useState('');
    const [sortBy, setSortBy] = useState('none');
    const [residues, setResidues] = useState([]);

    // filter application states
    const [applied, setApplied] = useState(false);
    const [resetApplied, setResetApplied] = useState(false);
    const [residueMenuOpen, setResidueMenuOpen] = useState(false);

    // map antigen types to dropdown options
    const antigenTypeOptions = [
        { key: 1, text: 'Default', value: '' },
        ...antigenTypes.map((type, index) => {
            return { key: index + 2, text: type, value: type };
        }
        )
    ];

    // FILTERING AND SORTING

    // apply filters and sort data based on user input
    const handleFilterSubmit = () => {
        let tempResponse = '';
        let filteredData = [...data];

        if (antigenType !== '') {
            filteredData = filteredData.filter(item => item.Antigen_type === antigenType);
            tempResponse += ' | Antigen Type: ' + antigenType;
        }
        if (repeatedUnits !== '') {
            filteredData = filteredData.filter(item => item.Repeat_unit_size === parseInt(repeatedUnits));
            tempResponse += ' | Repeat Unit Size: ' + repeatedUnits;
        }
        if (branched !== '') {
            filteredData = filteredData.filter(item => item.Branched === parseInt(branched));
            tempResponse += ' | Branched: ' + (branched === '1' ? 'Yes' : 'No');
        }
        if (residues.length > 0) {
            const resCountsFilter = databaseUtils.countResidues(residues);

            filteredData = filteredData.filter(item => { 
               
                const itemRes = databaseUtils.getResidueArray(item.Carbuilder_structure_format);
                const resCountsData = databaseUtils.countResidues(itemRes);
                // e.g. [(glc=>1), (fuc=>4)...

                return Object.keys(resCountsFilter).every(residue => {
                    return residue in resCountsData && resCountsFilter[residue] <= resCountsData[residue];
                });
            });
            tempResponse += ' | Residues: ' + residues.join(', ');
        }

        // Sorting
        if (sortBy === 'asc') {
            filteredData.sort((a, b) => a.Repeat_unit_size - b.Repeat_unit_size);
            tempResponse += ' | Sorted by ascending repeat unit size';
        } else if (sortBy === 'desc') {
            filteredData.sort((a, b) => b.Repeat_unit_size - a.Repeat_unit_size);
            tempResponse += ' | Sorted by descending repeat unit size';
        }

       // setSearch('');
        onFilterData(filteredData);

        if (filteredData.length === 0) {
            setFilterResponse('No results found for selected filters. Please try again.');
        } else {
            if (tempResponse === '') {
                setFilterResponse(''); // No filters applied
            } else {
                setFilterResponse('Found results for selected filters' + tempResponse);
            }
        }
    };


    // apply filters when data changes
    const applyFilter = () => {
         let filteredData = [...data];

        if (antigenType !== '') {
            filteredData = filteredData.filter(item => item.Antigen_type === antigenType);
        }
        if (repeatedUnits !== '') {
            filteredData = filteredData.filter(item => item.Repeat_unit_size === parseInt(repeatedUnits));
        }
        if (branched !== '') {
            filteredData = filteredData.filter(item => item.Branched === parseInt(branched));
        }
        if (residues.length > 0) {
            const resCountsFilter = databaseUtils.countResidues(residues);

            filteredData = filteredData.filter(item => { 
               
                const itemRes = databaseUtils.getResidueArray(item.Carbuilder_structure_format);
                const resCountsData = databaseUtils.countResidues(itemRes);
                // e.g. [(glc=>1), (fuc=>4)...

                return Object.keys(resCountsFilter).every(residue => {
                    return residue in resCountsData && resCountsFilter[residue] <= resCountsData[residue];
                });
            });

        }

        // Sorting
        if (sortBy === 'asc') {
            filteredData.sort((a, b) => a.Repeat_unit_size - b.Repeat_unit_size);
            
        } else if (sortBy === 'desc') {
            filteredData.sort((a, b) => b.Repeat_unit_size - a.Repeat_unit_size);
        }

       // setSearch('');
        onFilterData(filteredData);


        setApplied(true);
        setResetApplied(false);
    };

    // reset all filters to default, clear search, display all data
    const resetFilters = () => {
        setAntigenType('');
        setRepeatedUnits('');
        setBranched('');
        setSortBy('none');
        setResidues([]);
        setApplied(false);
        onFilterData(data);
        setFilterResponse('');
        setResetApplied(true);
        setSearch('');
    };


    // RESIDUE MENU HANDLING

    // close residue menu when filter modal is closed
    useEffect(() => {
        if (!openFilter) {
            setResidueMenuOpen(false);
        }
    }, [openFilter]);

     //update filter when data changes, e.g. when a species is selected/de-selected
    // FIX: Only auto-apply if filters are actually active to prevent re-filtering search results
    useEffect(() => {
       if (applied && !resetApplied) {
           applyFilter();
       }
       // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    //update filter when data changes, e.g. when a species is selected/de-selected
    useEffect(() => {
       if (clearFlag) {
            resetFilters();       // your custom reset logic
            setClearFlag(false);  // optionally reset the flag so it doesn't run again
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearFlag]); 

    return (
        <>
            <Button
                size="medium"
                icon
                active={openFilter}
                onClick={toggleFilterOptions}
                className="filter-toggle-button"
            >
                <Icon name="filter" /> Filter
            </Button>

            <FilterModal
                open={openFilter}
                onClose={toggleFilterOptions}
                branched={branched}
                setBranched={setBranched}
                antigenType={antigenType}
                setAntigenType={setAntigenType}
                repeatedUnits={repeatedUnits}
                setRepeatedUnits={setRepeatedUnits}
                sortBy={sortBy}
                setSortBy={setSortBy}
                residues={residues}
                setResidues={setResidues}
                handleFilterSubmit={handleFilterSubmit}
                resetFilters={resetFilters}
                maxRepeatUnit={maxRepeatUnit}
                antigenTypes={antigenTypes}
                applied={applied}
                resetApplied={resetApplied}
                residueMenuOpen={residueMenuOpen}
                setResidueMenuOpen={setResidueMenuOpen}
            />
        </>
    );
}
